import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, CONTACT_DETAILS } from "../data/site";
import { EASE } from "./Reveal";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";
import { WhatsAppButton } from "./WhatsAppButton";

const BrandLockup = ({ large = false }) => {
  const content = useContent();
  const logo = content?.site?.logo_url ? fileUrl(content.site.logo_url) : "/logo.png";
  return (
    <Link to="/" data-testid="brand-home-link" className="flex items-center" aria-label="Elin's Consultancy — Home">
      <img
        src={logo}
        alt="Elin's Consultancy"
        className={`w-auto ${large ? "h-24 sm:h-28" : "h-14 sm:h-16"}`}
      />
    </Link>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="sticky top-0 z-50 bg-forest border-b border-gold-soft/20"
    >
      <div className="wrap-x h-[84px] flex items-center justify-between gap-6">
        <BrandLockup />
        <nav data-testid="main-nav" className="hidden xl:flex items-center gap-5">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[12px] font-medium tracking-[0.03em] transition-colors duration-300 pb-1.5 ${
                  isActive ? "text-gold-soft" : "text-cream/80 hover:text-gold-soft"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-0 right-0 bottom-0 h-px bg-gold"
                      transition={{ duration: 0.4, ease: EASE }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <Link to="/contact" data-testid="header-book-button" className="btn-gold hidden md:inline-flex">
            Book a Consultation
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="xl:hidden text-cream p-2 -mr-2"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="xl:hidden overflow-hidden border-t border-gold-soft/15 bg-forest"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 0.05 + i * 0.05, ease: EASE }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 text-sm font-medium tracking-wide border-b border-cream/10 ${
                        isActive ? "text-gold-soft" : "text-cream/85"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/contact" data-testid="mobile-book-button" className="btn-gold mt-5 justify-center">
                Book a Consultation <ArrowRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

const Footer = () => {
  const content = useContent();
  const c = { ...CONTACT_DETAILS, ...(content?.contact || {}) };
  return (
    <footer data-testid="site-footer" className="bg-forest-deep text-cream/70">
      <div className="wrap-x pt-16 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-12 border-b border-gold-soft/15">
        <div>
          <BrandLockup large />
          <p className="text-[13px] leading-relaxed mt-6 max-w-xs text-cream/60">
            Independent tenant and leasehold advice. Property search and investor support.
          </p>
        </div>
        <div>
          <h3 className="text-gold-soft text-[11px] tracking-[0.28em] uppercase font-bold mb-5">Explore</h3>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`footer-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="flex items-center min-h-[44px] text-[13px] text-cream/60 hover:text-gold-soft transition-colors duration-300"
            >
              {link.label === "Tenant & Leasehold" ? "Tenant & Leasehold Advice" : link.label}
            </Link>
          ))}
        </div>
        <div>
          <h3 className="text-gold-soft text-[11px] tracking-[0.28em] uppercase font-bold mb-5">Services</h3>
          {["Tenant & Leasehold Advice", "Property Sourcing", "Refurbishment", "Professional Network"].map((s) => (
            <p key={s} className="text-[13px] leading-[2.2] text-cream/60">{s}</p>
          ))}
        </div>
        <div>
          <h3 className="text-gold-soft text-[11px] tracking-[0.28em] uppercase font-bold mb-5">Contact</h3>
          <a
            href={`mailto:${c.email}`}
            data-testid="footer-email-link"
            className="flex items-center min-h-[44px] text-[13px] text-cream/60 hover:text-gold-soft transition-colors duration-300"
          >
            {c.email}
          </a>
          <a
            href={`tel:${c.phone.replace(/[^+\d]/g, "")}`}
            data-testid="footer-phone-link"
            className="flex items-center min-h-[44px] text-[13px] text-cream/60 hover:text-gold-soft transition-colors duration-300"
          >
            {c.phone}
          </a>
          {c.address && <p className="text-[13px] leading-[2.2] text-cream/60">{c.address}</p>}
          <p className="text-[13px] leading-[2.2] text-cream/60">{c.area}</p>
        </div>
      </div>
      <div className="wrap-x py-7 flex flex-col sm:flex-row justify-between gap-3 text-[11.5px] tracking-[0.06em] text-cream/40">
        <span>© 2026 Elin&rsquo;s Consultancy. All rights reserved.</span>
        <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
          <Link
            to="/terms-and-conditions"
            data-testid="footer-terms-link"
            className="text-cream/60 hover:text-gold-soft transition-colors duration-300"
          >
            Terms &amp; Conditions
          </Link>
          <span className="text-cream/20">·</span>
          <Link
            to="/privacy-policy"
            data-testid="footer-privacy-link"
            className="text-cream/60 hover:text-gold-soft transition-colors duration-300"
          >
            Privacy Policy
          </Link>
          <span className="text-cream/20">·</span>
          <Link
            to="/complaints-procedure"
            data-testid="footer-complaints-link"
            className="text-cream/60 hover:text-gold-soft transition-colors duration-300"
          >
            Complaints Procedure
          </Link>
        </div>
        <Link
          to="/admin"
          data-testid="footer-admin-link"
          className="text-cream/30 hover:text-gold-soft transition-colors duration-300"
        >
          Admin Login
        </Link>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
    <WhatsAppButton />
  </div>
);
